import SectionContainer from "../SectionContainer";
import styles from "./RoomTypeSection.module.scss";
import PropertyFiltersSection from "./components/PropertyFiltersSection";
import useRoomTypeContext from "./context/useRoomTypeContext";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { lazy, Suspense, useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";
import { getCustomData, getRoomTypeTabData } from "./utils";
import AccordionV2 from "@Components/Accordian";
import CustomAccordionItem from "./components/CustomAccordianItem";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import FullPageLoader from "@Components/FullPageLoader";
import InfoIcon from "@Icons/InfoIcon";
import RoomTypesModal from "./components/RoomTypesModal";

// P1D-5512: loaded on first open, so the panel stays out of the property page's initial chunk.
const RoomDetailsSidePanel = lazy(() => import("./components/RoomDetailsSidePanel"));
//

const RoomTypeSection = ({ isDesktop = false }) => {
  const {
    roomTypes: { data = [], count = 0, isLoadingForFilter = false },
  } = useRoomTypeContext();

  const [activeTab, setActiveTab] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isRoomDetailsPanelOpen = useSelector(
    // Optional chaining: server state cached before this field existed has no `room_details_panel`.
    ({ propertyPageData }: AppState) => Boolean(propertyPageData.room_details_panel?.show),
  );
  const { t } = useTranslationHook();

  const tabdata = getRoomTypeTabData(data);
  const customData = getCustomData(tabdata, activeTab);
  const isRoomTypeV2 = true;

  if (isLoadingForFilter) {
    return (
      <SectionContainer
        title={t("propertypage.roomtypesectiondynamic.roomtypetitle")}
        count={count}
        id="room_type"
        className={formatClassNames(
          styles.container,
          isRoomTypeV2 && styles.containerV2,
          isDesktop && styles.desktop,
        )}
        isDesktop={isDesktop}
        dataTestId="property_page_room_types_section"
        headingLevel={2}
      >
        <FullPageLoader height="50vh" />
      </SectionContainer>
    );
  }

  return (
    <SectionContainer
      title={
        <span className={styles.titleWithIcon}>
          <span>
            {t("propertypage.roomtypesectiondynamic.roomtypetitle")}
            {count > 0 && <> ({count})</>}
          </span>
          <span
            className={styles.infoIconWrapper}
            onClick={() => setIsModalOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setIsModalOpen(true);
              }
            }}
          >
            <InfoIcon height="16" width="16" />
          </span>
        </span>
      }
      id="room_type"
      className={formatClassNames(
        styles.container,
        isRoomTypeV2 && styles.containerV2,
        isDesktop && styles.desktop,
      )}
      isDesktop={isDesktop}
      dataTestId="property_page_room_types_section"
      titleClassName={formatClassNames(styles.titlev2, isDesktop && styles.desktop)}
      headingLevel={2}
    >
      <PropertyFiltersSection
        tabdata={tabdata}
        setActiveTab={(val: any) => setActiveTab(val)}
        activeTab={activeTab}
        isDesktop={isDesktop}
      />

      <AccordionV2
        data={customData}
        CustomAccordionItem={CustomAccordionItem}
        isDesktop={isDesktop}
        activeTab={activeTab}
        sectionClassname={formatClassNames(
          isRoomTypeV2 && styles.sectionV2,
          isDesktop && styles.desktop,
        )}
      />

      <RoomTypesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        availableRoomTypes={Object.keys(tabdata).filter((key) => key !== "all")}
      />
      {/* P1D-5512: one panel for the whole list; opened by RoomTypeCardV2 for the test's variant. */}
      {isDesktop && isRoomDetailsPanelOpen && (
        <Suspense fallback={null}>
          <RoomDetailsSidePanel activeTab={activeTab} />
        </Suspense>
      )}
    </SectionContainer>
  );
};

export default wrapperHOC(RoomTypeSection, {
  componentName: "RoomTypeSection-PropertyPage",
  showForChina: true,
});
